import { useState } from "react";
import { motion } from "framer-motion";
import { ParticleField, MorphBlob, NodeNetwork, FlowLines } from "@/components/effects/DynamicBackgrounds";
import { SEOHead } from "@/components/seo/SEOHead";
import { 
  Mail, 
  Phone, 
  MapPin, 
  Send,
  CheckCircle,
  Calendar,
  Users,
  Building2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

export const ContactPage = () => {
  const [formType, setFormType] = useState("demo"); // demo or contact
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const [demoForm, setDemoForm] = useState({
    name: "",
    email: "",
    company: "",
    job_title: "",
    phone: "",
    company_size: "",
    interest: "",
    message: ""
  });

  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    message: ""
  });

  const handleDemoSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/demo-request`, demoForm);
      setSubmitted(true);
      toast.success("Demo request submitted! We'll be in touch soon.");
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, { ...contactForm, form_type: "contact" });
      setSubmitted(true);
      toast.success("Message sent! We'll get back to you shortly.");
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-[#020617] pt-20 min-h-screen">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <div className="w-20 h-20 mx-auto rounded-full bg-[#10B981]/20 flex items-center justify-center mb-6">
              <CheckCircle className="w-10 h-10 text-[#10B981]" />
            </div>
            <h1 className="text-3xl font-bold text-[#F8FAFC] mb-4">Thank You!</h1>
            <p className="text-[#94A3B8] mb-8">
              {formType === "demo" 
                ? "Your demo request has been received. Our team will contact you within 24 hours to schedule your personalized demo."
                : "Your message has been sent. We'll get back to you as soon as possible."
              }
            </p>
            <Button 
              onClick={() => setSubmitted(false)}
              variant="outline"
              className="border-[#00B4D8] text-[#00B4D8] hover:bg-[#00B4D8]/10"
              data-testid="submit-another-btn"
            >
              Submit Another Request
            </Button>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#020617] pt-20">
      <SEOHead
        title="Contact"
        description="Get in touch with ProcureAide. Book a demo, request a consultation, or enquire about our AI-powered procurement intelligence platform for organisations in Pakistan and Canada."
        path="/contact"
      />
      {/* Hero */}
      <section className="py-16 relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern-animated opacity-30" />
        <ParticleField count={18} />
        <MorphBlob color="rgba(0,180,216,0.06)" size="450px" className="top-[5%] right-[-10%]" />
        <MorphBlob color="rgba(10,36,99,0.1)" size="350px" className="bottom-[10%] left-[-8%]" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="text-center max-w-3xl mx-auto"
          >
            <motion.p 
              variants={fadeInUp}
              className="text-xs uppercase tracking-[0.2em] text-[#00B4D8] mb-4"
            >
              Get in Touch
            </motion.p>
            <motion.h1 
              variants={fadeInUp}
              className="text-4xl sm:text-5xl font-bold text-[#F8FAFC] leading-tight tracking-tight mb-6"
            >
              Let's Transform Your{" "}
              <span className="text-[#00B4D8]">Procurement</span>
            </motion.h1>
            <motion.p 
              variants={fadeInUp}
              className="text-lg text-[#94A3B8]"
            >
              Book a demo, ask a question, or explore how Procureaide can help your organization.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-16 relative overflow-hidden">
        <FlowLines count={3} className="opacity-20" />
        <NodeNetwork count={8} className="opacity-15" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="text-2xl font-bold text-[#F8FAFC] mb-6">Contact Information</h2>
                
                <div className="space-y-6 mb-8">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-[#00B4D8]/10 flex items-center justify-center flex-shrink-0">
                      <Mail className="w-5 h-5 text-[#00B4D8]" />
                    </div>
                    <div>
                      <p className="text-sm text-[#64748B]">Email</p>
                      <a href="mailto:info@procureaide.com" className="text-[#F8FAFC] hover:text-[#00B4D8]">
                        info@procureaide.com
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-[#00B4D8]/10 flex items-center justify-center flex-shrink-0">
                      <Phone className="w-5 h-5 text-[#00B4D8]" />
                    </div>
                    <div>
                      <p className="text-sm text-[#64748B]">Phone</p>
                      <a href="tel:+1234567890" className="text-[#F8FAFC] hover:text-[#00B4D8]">
                        +1 (234) 567-890
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-[#00B4D8]/10 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-[#00B4D8]" />
                    </div>
                    <div>
                      <p className="text-sm text-[#64748B]">Location</p>
                      <p className="text-[#F8FAFC]">Global — Remote First</p>
                    </div>
                  </div>
                </div>

                <div className="bg-[#0B1426] border border-[#00B4D8]/20 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-[#F8FAFC] mb-4">What to Expect</h3>
                  <ul className="space-y-3">
                    <li className="flex items-center gap-3 text-sm text-[#94A3B8]">
                      <Calendar className="w-4 h-4 text-[#00B4D8]" />
                      Response within 24 hours
                    </li>
                    <li className="flex items-center gap-3 text-sm text-[#94A3B8]">
                      <Users className="w-4 h-4 text-[#00B4D8]" />
                      Personalized demo walkthrough
                    </li>
                    <li className="flex items-center gap-3 text-sm text-[#94A3B8]">
                      <Building2 className="w-4 h-4 text-[#00B4D8]" />
                      Tailored to your industry
                    </li>
                  </ul>
                </div>
              </motion.div>
            </div>

            {/* Form */}
            <div className="lg:col-span-2">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                className="bg-[#0B1426] border border-[#00B4D8]/20 rounded-2xl p-8"
              >
                {/* Form Type Toggle */}
                <div className="flex gap-4 mb-8">
                  <button
                    onClick={() => setFormType("demo")}
                    className={`flex-1 py-3 rounded-lg font-medium transition-all ${
                      formType === "demo"
                        ? "bg-[#00B4D8] text-[#020617]"
                        : "bg-[#111F36] text-[#94A3B8] hover:text-[#F8FAFC]"
                    }`}
                    data-testid="form-type-demo"
                  >
                    Book a Demo
                  </button>
                  <button
                    onClick={() => setFormType("contact")}
                    className={`flex-1 py-3 rounded-lg font-medium transition-all ${
                      formType === "contact"
                        ? "bg-[#00B4D8] text-[#020617]"
                        : "bg-[#111F36] text-[#94A3B8] hover:text-[#F8FAFC]"
                    }`}
                    data-testid="form-type-contact"
                  >
                    General Inquiry
                  </button>
                </div>

                {formType === "demo" ? (
                  <form onSubmit={handleDemoSubmit} className="space-y-6">
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="name" className="text-[#F8FAFC]">Full Name *</Label>
                        <Input
                          id="name"
                          required
                          value={demoForm.name}
                          onChange={(e) => setDemoForm({ ...demoForm, name: e.target.value })}
                          className="mt-2 bg-[#111F36] border-[#00B4D8]/30 focus:border-[#00B4D8] text-[#F8FAFC]"
                          placeholder="John Doe"
                          data-testid="demo-name-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="email" className="text-[#F8FAFC]">Work Email *</Label>
                        <Input
                          id="email"
                          type="email"
                          required
                          value={demoForm.email}
                          onChange={(e) => setDemoForm({ ...demoForm, email: e.target.value })}
                          className="mt-2 bg-[#111F36] border-[#00B4D8]/30 focus:border-[#00B4D8] text-[#F8FAFC]"
                          placeholder="john@company.com"
                          data-testid="demo-email-input"
                        />
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="company" className="text-[#F8FAFC]">Company *</Label>
                        <Input
                          id="company"
                          required
                          value={demoForm.company}
                          onChange={(e) => setDemoForm({ ...demoForm, company: e.target.value })}
                          className="mt-2 bg-[#111F36] border-[#00B4D8]/30 focus:border-[#00B4D8] text-[#F8FAFC]"
                          placeholder="Acme Corp"
                          data-testid="demo-company-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="job_title" className="text-[#F8FAFC]">Job Title</Label>
                        <Input
                          id="job_title"
                          value={demoForm.job_title}
                          onChange={(e) => setDemoForm({ ...demoForm, job_title: e.target.value })}
                          className="mt-2 bg-[#111F36] border-[#00B4D8]/30 focus:border-[#00B4D8] text-[#F8FAFC]"
                          placeholder="Procurement Director"
                          data-testid="demo-jobtitle-input"
                        />
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="company_size" className="text-[#F8FAFC]">Company Size</Label>
                        <select
                          id="company_size"
                          value={demoForm.company_size}
                          onChange={(e) => setDemoForm({ ...demoForm, company_size: e.target.value })}
                          className="mt-2 flex h-9 w-full rounded-md border border-[#00B4D8]/30 bg-[#111F36] px-3 py-2 text-sm text-[#F8FAFC] shadow-sm focus:outline-none focus:ring-1 focus:ring-[#00B4D8] [&>option]:bg-[#111F36] [&>option]:text-[#F8FAFC]"
                          data-testid="demo-companysize-select"
                        >
                          <option value="" disabled className="text-[#94A3B8]">Select size</option>
                          <option value="1-50">1-50 employees</option>
                          <option value="51-200">51-200 employees</option>
                          <option value="201-500">201-500 employees</option>
                          <option value="501-1000">501-1000 employees</option>
                          <option value="1000+">1000+ employees</option>
                        </select>
                      </div>
                      <div>
                        <Label htmlFor="interest" className="text-[#F8FAFC]">Primary Interest</Label>
                        <select
                          id="interest"
                          value={demoForm.interest}
                          onChange={(e) => setDemoForm({ ...demoForm, interest: e.target.value })}
                          className="mt-2 flex h-9 w-full rounded-md border border-[#00B4D8]/30 bg-[#111F36] px-3 py-2 text-sm text-[#F8FAFC] shadow-sm focus:outline-none focus:ring-1 focus:ring-[#00B4D8] [&>option]:bg-[#111F36] [&>option]:text-[#F8FAFC]"
                          data-testid="demo-interest-select"
                        >
                          <option value="" disabled className="text-[#94A3B8]">Select module</option>
                          <option value="full_platform">Full Platform</option>
                          <option value="data_refinery">DataRefinery</option>
                          <option value="spendlens">SpendLens</option>
                          <option value="contrack">ConTrack</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="message" className="text-[#F8FAFC]">Additional Information</Label>
                      <Textarea
                        id="message"
                        value={demoForm.message}
                        onChange={(e) => setDemoForm({ ...demoForm, message: e.target.value })}
                        className="mt-2 bg-[#111F36] border-[#00B4D8]/30 focus:border-[#00B4D8] text-[#F8FAFC] min-h-[100px]"
                        placeholder="Tell us about your procurement challenges..."
                        data-testid="demo-message-input"
                      />
                    </div>

                    <Button 
                      type="submit"
                      disabled={loading}
                      className="w-full bg-[#00B4D8] text-[#020617] font-bold hover:bg-[#0096B4] transition-all py-6"
                      data-testid="demo-submit-btn"
                    >
                      {loading ? "Submitting..." : "Request Demo"}
                      <Send className="ml-2 w-5 h-5" />
                    </Button>
                  </form>
                ) : (
                  <form onSubmit={handleContactSubmit} className="space-y-6">
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="contact_name" className="text-[#F8FAFC]">Full Name *</Label>
                        <Input
                          id="contact_name"
                          required
                          value={contactForm.name}
                          onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                          className="mt-2 bg-[#111F36] border-[#00B4D8]/30 focus:border-[#00B4D8] text-[#F8FAFC]"
                          placeholder="John Doe"
                          data-testid="contact-name-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="contact_email" className="text-[#F8FAFC]">Email *</Label>
                        <Input
                          id="contact_email"
                          type="email"
                          required
                          value={contactForm.email}
                          onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                          className="mt-2 bg-[#111F36] border-[#00B4D8]/30 focus:border-[#00B4D8] text-[#F8FAFC]"
                          placeholder="john@example.com"
                          data-testid="contact-email-input"
                        />
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="contact_company" className="text-[#F8FAFC]">Company</Label>
                        <Input
                          id="contact_company"
                          value={contactForm.company}
                          onChange={(e) => setContactForm({ ...contactForm, company: e.target.value })}
                          className="mt-2 bg-[#111F36] border-[#00B4D8]/30 focus:border-[#00B4D8] text-[#F8FAFC]"
                          placeholder="Acme Corp"
                          data-testid="contact-company-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="contact_phone" className="text-[#F8FAFC]">Phone</Label>
                        <Input
                          id="contact_phone"
                          value={contactForm.phone}
                          onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                          className="mt-2 bg-[#111F36] border-[#00B4D8]/30 focus:border-[#00B4D8] text-[#F8FAFC]"
                          placeholder="+1 234 567 890"
                          data-testid="contact-phone-input"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="contact_message" className="text-[#F8FAFC]">Message *</Label>
                      <Textarea
                        id="contact_message"
                        required
                        value={contactForm.message}
                        onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                        className="mt-2 bg-[#111F36] border-[#00B4D8]/30 focus:border-[#00B4D8] text-[#F8FAFC] min-h-[150px]"
                        placeholder="How can we help you?"
                        data-testid="contact-message-input"
                      />
                    </div>

                    <Button 
                      type="submit"
                      disabled={loading}
                      className="w-full bg-[#00B4D8] text-[#020617] font-bold hover:bg-[#0096B4] transition-all py-6"
                      data-testid="contact-submit-btn"
                    >
                      {loading ? "Sending..." : "Send Message"}
                      <Send className="ml-2 w-5 h-5" />
                    </Button>
                  </form>
                )}
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
